/**
 * Cooperative portal theme utilities for the internal admin app.
 *
 * Used only for preview rendering, validation, and contrast warnings —
 * NOT for applying theme to the admin shell (no documentElement mutations).
 */

export const DEFAULT_THEME = {
  primary: "#8B313A",
  secondary: "#31778C",
};

const HEX_COLOR_REGEX = /^#[0-9A-Fa-f]{6}$/;

export function isValidHexColor(value) {
  return typeof value === "string" && HEX_COLOR_REGEX.test(value.trim());
}

export function normalizeHexColor(value, fallback) {
  if (!value) return fallback;
  const trimmed = String(value).trim();
  const withHash = trimmed.startsWith("#") ? trimmed : `#${trimmed}`;
  return isValidHexColor(withHash) ? withHash.toUpperCase() : fallback;
}

export function normalizeTheme(source) {
  if (!source) return { ...DEFAULT_THEME };

  const themeBlock = source.theme || source;
  return {
    primary: normalizeHexColor(
      themeBlock.primary_color ?? themeBlock.primary ?? source.primary_color ?? source.border_color,
      DEFAULT_THEME.primary
    ),
    secondary: normalizeHexColor(
      themeBlock.secondary_color ??
        themeBlock.secondary ??
        source.secondary_color ??
        source.background_color,
      DEFAULT_THEME.secondary
    ),
  };
}

export function hexToRgba(hex, alpha = 1) {
  const normalized = normalizeHexColor(hex, DEFAULT_THEME.primary).replace("#", "");
  const r = parseInt(normalized.slice(0, 2), 16);
  const g = parseInt(normalized.slice(2, 4), 16);
  const b = parseInt(normalized.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function hexToRgb(hex) {
  const normalized = normalizeHexColor(hex, DEFAULT_THEME.primary).replace("#", "");
  return {
    r: parseInt(normalized.slice(0, 2), 16),
    g: parseInt(normalized.slice(2, 4), 16),
    b: parseInt(normalized.slice(4, 6), 16),
  };
}

function rgbToHex({ r, g, b }) {
  return `#${[r, g, b]
    .map((c) =>
      Math.max(0, Math.min(255, Math.round(c)))
        .toString(16)
        .padStart(2, "0")
    )
    .join("")}`.toUpperCase();
}

function relativeLuminance({ r, g, b }) {
  const channels = [r, g, b].map((channel) => {
    const normalized = channel / 255;
    return normalized <= 0.03928
      ? normalized / 12.92
      : ((normalized + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2];
}

/** WCAG contrast ratio between two hex colors (1–21). */
export function getContrastRatio(hexA, hexB) {
  const l1 = relativeLuminance(hexToRgb(hexA));
  const l2 = relativeLuminance(hexToRgb(hexB));
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
}

/** Pick white or dark text for readability on a colored background. */
export function getReadableTextColor(backgroundHex) {
  const whiteContrast = getContrastRatio(backgroundHex, "#FFFFFF");
  const darkContrast = getContrastRatio(backgroundHex, "#383838");
  return whiteContrast >= darkContrast ? "#FFFFFF" : "#383838";
}

/** True when used as text on a white background fails WCAG AA (4.5:1). */
export function hasPoorContrastWithWhite(hex) {
  return isValidHexColor(hex) && getContrastRatio(hex, "#FFFFFF") < 4.5;
}

/** Relative luminance above ~mid-grey — useful for detecting light brand colors. */
export function isLightColor(hex) {
  const normalized = normalizeHexColor(hex, DEFAULT_THEME.primary);
  return relativeLuminance(hexToRgb(normalized)) > 0.6;
}

/**
 * Darken a hex color. `amount` is 0–1 (higher = darker).
 * Original brand color is never mutated in storage — derived tokens only.
 */
export function darkenColor(hex, amount = 0.15) {
  const normalized = normalizeHexColor(hex, DEFAULT_THEME.primary);
  const { r, g, b } = hexToRgb(normalized);
  const factor = Math.max(0, 1 - amount);
  return rgbToHex({
    r: r * factor,
    g: g * factor,
    b: b * factor,
  });
}

const READABLE_ON_WHITE_MIN_RATIO = 4.5;

/**
 * Returns the original hex when it meets contrast on white; otherwise a darkened readable variant.
 * Does not replace the saved brand color — use for text/links on white surfaces in preview.
 */
export function getReadableColorOnWhite(hex, minRatio = READABLE_ON_WHITE_MIN_RATIO) {
  const normalized = normalizeHexColor(hex, DEFAULT_THEME.primary);
  if (getContrastRatio(normalized, "#FFFFFF") >= minRatio) {
    return normalized;
  }
  let current = normalized;
  for (let step = 0; step < 24; step += 1) {
    current = darkenColor(current, 0.08);
    if (getContrastRatio(current, "#FFFFFF") >= minRatio) {
      return current;
    }
  }
  return current;
}

/** Build CSS custom properties for scoped preview containers (not :root). */
export function buildThemeCssVariablesFromColors(primaryColor, secondaryColor) {
  return buildThemeCssVariables({
    primary: normalizeHexColor(primaryColor, DEFAULT_THEME.primary),
    secondary: normalizeHexColor(secondaryColor, DEFAULT_THEME.secondary),
  });
}

export function buildThemeCssVariables(theme) {
  const { primary, secondary } = normalizeTheme(theme);
  const primaryReadable = getReadableColorOnWhite(primary);
  const secondaryReadable = getReadableColorOnWhite(secondary);

  return {
    "--color-primary": primary,
    "--color-secondary": secondary,
    "--color-primary-soft": hexToRgba(primary, 0.1),
    "--color-secondary-soft": hexToRgba(secondary, 0.1),
    "--color-border": hexToRgba(primary, 0.2),
    "--color-link": secondaryReadable,
    "--color-accent": secondaryReadable,
    "--color-primary-dark": `color-mix(in srgb, ${primary} 80%, black)`,
    "--color-secondary-dark": `color-mix(in srgb, ${secondary} 80%, black)`,
    "--color-primary-readable": primaryReadable,
    "--color-secondary-readable": secondaryReadable,
    "--color-primary-text": getReadableTextColor(primary),
    "--color-secondary-text": getReadableTextColor(secondary),
    "--primary": primary,
    "--secondary2": secondary,
  };
}

export function themeToApiPayload(theme) {
  const normalized = normalizeTheme(theme);
  return {
    primary_color: normalized.primary,
    secondary_color: normalized.secondary,
  };
}
